import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useGuestGate } from "../lib/useGuestGate";
import { summarizeTrades, money, SHARES_PER_CONTRACT } from "../lib/history";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  Save,
  TrendingDown,
  Repeat,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  kind: "put",
  ticker: "",
  premium: "",
  purchase_price: "",
  sale_price: "",
  dividends: "",
};

function KindBadge({ kind }) {
  const isPut = kind === "put";
  const Icon = isPut ? TrendingDown : Repeat;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border ${
        isPut
          ? "bg-blue-50 text-blue-700 border-blue-200"
          : "bg-emerald-50 text-emerald-700 border-emerald-200"
      }`}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {isPut ? "Sold Put" : "Covered Call"}
    </span>
  );
}

export default function HistoryYearPage() {
  const { gate } = useGuestGate();
  const { yearId } = useParams();
  const [year, setYear] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  const [carInput, setCarInput] = useState("");
  const [savingCar, setSavingCar] = useState(false);

  const [mode, setMode] = useState(null); // "create" | "edit" | null
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [yRes, tRes] = await Promise.all([
        api.get(`/trade-years/${yearId}`),
        api.get(`/trade-years/${yearId}/trades`),
      ]);
      setYear(yRes.data);
      setCarInput(String(yRes.data.car ?? 0));
      setTrades(tRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load year");
    } finally {
      setLoading(false);
    }
  }, [yearId]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(
    () => summarizeTrades(trades, year?.car ?? 0),
    [trades, year]
  );

  const saveCar = async (e) => {
    e?.preventDefault?.();
    if (gate("Saving CAR")) return;
    setSavingCar(true);
    try {
      const { data } = await api.patch(`/trade-years/${yearId}`, {
        car: Number(carInput || 0),
      });
      setYear(data);
      toast.success("CAR updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update CAR");
    } finally {
      setSavingCar(false);
    }
  };

  const openCreate = () => {
    if (gate("Adding a trade")) return;
    setEditingId(null);
    setForm(EMPTY_FORM);
    setMode("create");
  };

  const openEdit = (t) => {
    if (gate("Editing a trade")) return;
    setEditingId(t.id);
    setForm({
      kind: t.kind,
      ticker: t.ticker,
      premium: String(t.premium ?? ""),
      purchase_price: t.purchase_price == null ? "" : String(t.purchase_price),
      sale_price: t.sale_price == null ? "" : String(t.sale_price),
      dividends: t.dividends == null ? "" : String(t.dividends),
    });
    setMode("edit");
  };

  const closeDialog = () => {
    setMode(null);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ticker.trim()) return;
    setSubmitting(true);
    const body = {
      kind: form.kind,
      ticker: form.ticker.trim().toUpperCase(),
      premium: Number(form.premium || 0),
    };
    if (form.kind === "covered_call") {
      body.purchase_price = form.purchase_price === "" ? null : Number(form.purchase_price);
      body.sale_price = form.sale_price === "" ? null : Number(form.sale_price);
      body.dividends = form.dividends === "" ? null : Number(form.dividends);
    } else {
      // For puts, clear the covered-call-only fields
      body.purchase_price = null;
      body.sale_price = null;
      body.dividends = null;
    }
    try {
      if (mode === "edit" && editingId) {
        const { data } = await api.put(`/trades/${editingId}`, body);
        setTrades((prev) => prev.map((t) => (t.id === editingId ? data : t)));
        toast.success(`${data.ticker} updated`);
      } else {
        const { data } = await api.post(`/trade-years/${yearId}/trades`, body);
        setTrades((prev) => [...prev, data]);
        toast.success(`${data.ticker} added`);
      }
      closeDialog();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save trade");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, ticker) => {
    if (gate("Removing a trade")) return;
    try {
      await api.delete(`/trades/${id}`);
      setTrades((prev) => prev.filter((t) => t.id !== id));
      toast.success(`${ticker} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove trade");
    } finally {
      setConfirmTarget(null);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (loading) {
    return (
      <div className="py-16 text-center text-sm text-slate-500" data-testid="year-loading">
        Loading...
      </div>
    );
  }
  if (!year) {
    return (
      <div className="py-16 text-center text-sm text-slate-500">Year not found.</div>
    );
  }

  const carUnchanged = Number(carInput || 0) === Number(year.car ?? 0);

  return (
    <div className="space-y-8" data-testid="history-year-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/history"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="back-to-history"
        >
          <ChevronLeft className="w-4 h-4" /> Back to History
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* CAR card */}
        <form
          onSubmit={saveCar}
          className="bg-white border border-slate-200 rounded-xl shadow-sm p-6"
          data-testid="car-card"
        >
          <div className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-2">
            Capital At Risk
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-lg">$</span>
            <Input
              type="number"
              inputMode="decimal"
              step="any"
              min={0}
              value={carInput}
              onChange={(e) => setCarInput(e.target.value)}
              className="font-mono tabular-nums text-2xl h-12 font-semibold"
              data-testid="car-input"
            />
          </div>
          <div className="text-xs text-slate-400 mt-1">per option</div>
          <Button
            type="submit"
            disabled={savingCar || carUnchanged}
            className="mt-4 w-full bg-slate-900 hover:bg-slate-800 text-white gap-2"
            data-testid="save-car-button"
          >
            <Save className="w-4 h-4" />
            {savingCar ? "Saving..." : carUnchanged ? "Saved" : "Save CAR"}
          </Button>
        </form>

        {/* P/L card */}
        <div
          className="bg-slate-900 text-white rounded-xl shadow-sm p-6"
          data-testid="pl-card"
        >
          <div className="text-xs font-medium tracking-[0.2em] uppercase text-slate-400 mb-2">
            Total P/L
          </div>
          <div
            className={`font-heading font-black tracking-tighter text-4xl sm:text-5xl leading-none tabular-nums ${
              summary.totalPL < 0 ? "text-red-400" : ""
            }`}
            data-testid="total-pl"
          >
            {money(summary.totalPL)}
          </div>
          <div
            className="mt-3 text-sm tabular-nums"
            data-testid="pct-of-car"
          >
            {summary.pct === null ? (
              <span className="text-slate-400">Set CAR to see % return</span>
            ) : (
              <span
                className={summary.pct >= 0 ? "text-emerald-400" : "text-red-400"}
              >
                {summary.pct >= 0 ? "+" : ""}
                {summary.pct.toFixed(2)}% of CAR
              </span>
            )}
          </div>
        </div>

        {/* Breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-3">
            Breakdown
          </div>
          <div className="space-y-2 text-sm">
            <Row label="Premiums × 100" value={money(summary.premiums)} />
            <Row label="Dividends × 100" value={money(summary.dividends)} />
            <Row label="Assignment P/L" value={money(summary.assignmentPL)} />
            <div className="pt-2 mt-2 border-t border-slate-100">
              <Row label="Trades" value={`${trades.length}`} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-1">
            Trade Log
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 font-heading">
            Year {year.year}
          </h2>
        </div>
        <Button
          onClick={openCreate}
          className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
          data-testid="add-trade-button"
        >
          <Plus className="w-4 h-4" />
          Add Trade
        </Button>
      </div>

      <Dialog open={mode !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="sm:max-w-md" data-testid="trade-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {mode === "edit" ? "Edit Trade" : "Add Trade"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-slate-600">Kind</Label>
              <Select
                value={form.kind}
                onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}
              >
                <SelectTrigger className="mt-1.5" data-testid="trade-kind-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="put" data-testid="trade-kind-put">Sold Put</SelectItem>
                  <SelectItem value="covered_call" data-testid="trade-kind-cc">Sold Covered Call</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label
                  htmlFor="t-ticker"
                  className="text-xs tracking-[0.15em] uppercase text-slate-600"
                >
                  Ticker
                </Label>
                <Input
                  id="t-ticker"
                  required
                  value={form.ticker}
                  onChange={(e) => setForm((f) => ({ ...f, ticker: e.target.value.toUpperCase() }))}
                  placeholder="AAPL"
                  maxLength={10}
                  className="mt-1.5 font-mono uppercase"
                  data-testid="trade-ticker-input"
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <Label
                    htmlFor="t-premium"
                    className="text-xs tracking-[0.15em] uppercase text-slate-600"
                  >
                    Premium / share
                  </Label>
                  <span className="text-xs text-slate-400">$</span>
                </div>
                <Input
                  id="t-premium"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  required
                  value={form.premium}
                  onChange={set("premium")}
                  placeholder="0.48"
                  className="mt-1.5 font-mono tabular-nums"
                  data-testid="trade-premium-input"
                />
              </div>
            </div>

            {form.kind === "covered_call" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-baseline justify-between">
                      <Label
                        htmlFor="t-purchase"
                        className="text-xs tracking-[0.15em] uppercase text-slate-600"
                      >
                        Purchase Price
                      </Label>
                      <span className="text-xs text-slate-400">$</span>
                    </div>
                    <Input
                      id="t-purchase"
                      type="number"
                      inputMode="decimal"
                      step="any"
                      min={0}
                      value={form.purchase_price}
                      onChange={set("purchase_price")}
                      placeholder="optional"
                      className="mt-1.5 font-mono tabular-nums"
                      data-testid="trade-purchase-input"
                    />
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between">
                      <Label
                        htmlFor="t-sale"
                        className="text-xs tracking-[0.15em] uppercase text-slate-600"
                      >
                        Sale Price
                      </Label>
                      <span className="text-xs text-slate-400">$</span>
                    </div>
                    <Input
                      id="t-sale"
                      type="number"
                      inputMode="decimal"
                      step="any"
                      min={0}
                      value={form.sale_price}
                      onChange={set("sale_price")}
                      placeholder="optional"
                      className="mt-1.5 font-mono tabular-nums"
                      data-testid="trade-sale-input"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-baseline justify-between">
                    <Label
                      htmlFor="t-div"
                      className="text-xs tracking-[0.15em] uppercase text-slate-600"
                    >
                      Dividends Received / share
                    </Label>
                    <span className="text-xs text-slate-400">$</span>
                  </div>
                  <Input
                    id="t-div"
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min={0}
                    value={form.dividends}
                    onChange={set("dividends")}
                    placeholder="optional"
                    className="mt-1.5 font-mono tabular-nums"
                    data-testid="trade-dividends-input"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Each value is per share. Money received is multiplied by {SHARES_PER_CONTRACT} per contract.
                </p>
              </>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                data-testid="cancel-trade-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="submit-trade-button"
              >
                {submitting ? "Saving..." : mode === "edit" ? "Save Changes" : "Add Trade"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div
        className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
        data-testid="trades-table"
      >
        <table className="w-full min-w-[820px] table-auto">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Ticker</th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Kind</th>
              <th className="text-right py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Premium /sh</th>
              <th className="text-right py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Purchase</th>
              <th className="text-right py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Sale</th>
              <th className="text-right py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Div /sh</th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {trades.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-16 text-center" data-testid="empty-trades">
                  <div className="text-sm text-slate-500">No trades yet.</div>
                  <div className="text-xs text-slate-400 mt-1">
                    Click "Add Trade" to log your first one.
                  </div>
                </td>
              </tr>
            ) : (
              trades.map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                  data-testid={`trade-row-${t.id}`}
                >
                  <td className="py-4 px-6">
                    <span className="font-mono font-semibold text-slate-900 text-base tracking-wide">
                      {t.ticker}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <KindBadge kind={t.kind} />
                  </td>
                  <td className="py-4 px-4 text-right font-mono tabular-nums text-sm text-slate-700">
                    {money(t.premium)}
                  </td>
                  <td className="py-4 px-4 text-right font-mono tabular-nums text-sm text-slate-700">
                    {t.purchase_price == null ? <span className="text-slate-300">—</span> : money(t.purchase_price)}
                  </td>
                  <td className="py-4 px-4 text-right font-mono tabular-nums text-sm text-slate-700">
                    {t.sale_price == null ? <span className="text-slate-300">—</span> : money(t.sale_price)}
                  </td>
                  <td className="py-4 px-4 text-right font-mono tabular-nums text-sm text-slate-700">
                    {t.dividends == null ? <span className="text-slate-300">—</span> : money(t.dividends)}
                  </td>
                  <td className="py-4 px-6 text-right w-[120px]">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => openEdit(t)}
                        aria-label={`Edit ${t.ticker}`}
                        title={`Edit ${t.ticker}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors"
                        data-testid={`edit-trade-${t.id}`}
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => { if (gate("Removing a trade")) return; setConfirmTarget({ id: t.id, ticker: t.ticker }); }}
                        aria-label={`Remove ${t.ticker}`}
                        title={`Remove ${t.ticker}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                        data-testid={`delete-trade-${t.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog
        open={confirmTarget !== null}
        onOpenChange={(o) => !o && setConfirmTarget(null)}
      >
        <AlertDialogContent data-testid="delete-trade-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove trade?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete the trade on{" "}
              <span className="font-mono text-slate-800">{confirmTarget?.ticker}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-trade">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmTarget && handleDelete(confirmTarget.id, confirmTarget.ticker)
              }
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-trade"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs tracking-[0.15em] uppercase text-slate-500">
        {label}
      </span>
      <span className="font-mono tabular-nums text-slate-800">{value}</span>
    </div>
  );
}
